import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import "./DataSetRatio.css"

/**
 * Displays a visual representation of dataset ratios using a segmented bar chart and legend.
 *
 * Each segment in the chart corresponds to a "state" with a specific value and label,
 * and is color-coded for clarity. Includes dynamic calculation of percentages and
 * conditionally renders labels and legends based on values.
 *
 * @component
 *
 * @param {Object} props - The component props.
 * @param {string} props.heading - Title displayed at the top of the chart section.
 * @param {string} props.label - Subtitle describing the chart or dataset.
 * @param {Array} props.states - Array of state objects used to render chart segments and legends.
 * @param {string} props.states[].name - Label for the legend and tooltip.
 * @param {number} props.states[].value - Numeric value used to calculate percentage and segment width.
 * @param {boolean} [props.states[].selected] - Indicates if the current user is part of this state (e.g., show "(w tym Ty)").
 *
 * @returns {JSX.Element} The rendered dataset ratio component.
 */
const DataSetRatio = ({ heading, label, states }) => {
    const totalValue = useMemo(() => {
        return states.reduce((accumulator, current) => {
            return accumulator + current.value;
        }, 0);
    }, [states]);

    const [modifierRed, setModifierRed] = useState(76);
    const [modifierGreen, setModifierGreen] = useState(0);
    const [modifierBlue, setModifierBlue] = useState(61);

    const [baseRed, setBaseRed] = useState(295);
    const [baseGreen, setBaseGreen] = useState(0);
    const [baseBlue, setBaseBlue] = useState(193);

    const getBorderRadius = (index, statesLength) => {
        if (statesLength === 1) {
            return "0.5rem";
        }

        if (index === 0) {
            return "0.5rem 0 0 0.5rem";
        }

        if (index === statesLength - 1) {
            return "0 0.5rem 0.5rem 0";
        }

        return "";
    }

    const chartLegend = () => {
        let red = baseRed;
        let green = baseGreen;
        let blue = baseBlue;

        return states.map((state, index) => {
            if (state.value === 0) {
                return;
            }

            red -= modifierRed;
            green -= modifierGreen;
            blue -= modifierBlue;

            return (
                <div key={index} className="data-set-ratio__legend">
                    <div className="data-set-ratio__legend-color-item" style={{ backgroundColor: `rgb(${red}, ${green}, ${blue})` }}></div>
                    <div className="data-set-ratio__legend-color-text">
                        <p className="m-0">{state.name}</p>
                        <p className="m-0">
                            <span className="bold">{state.value}</span>
                            <span> osób {state.selected && "(w tym Ty)"}</span>
                        </p>
                    </div>
                </div>
            );
        });
    };

    const chart = () => {
        let red = baseRed;
        let green = baseGreen;
        let blue = baseBlue;

        return states.map((state, index) => {
            if (state.value === 0) {
                return;
            }

            red -= modifierRed;
            green -= modifierGreen;
            blue -= modifierBlue;

            const procentTemp = Math.round((state.value / totalValue) * 100);
            const procent = procentTemp <= 0 ? 1 : procentTemp;
            return (
                <div key={index} className="chart-segment"
                    style={{
                        backgroundColor: `rgb(${red}, ${green}, ${blue})`,
                        width: `${procent}%`,
                        borderRadius: getBorderRadius(index, states.length),
                        borderLeft: index !== 0 && index !== states.length ? "solid 0.25rem #fff" : "",
                    }}>
                </div>
            );
        });
    }

    return (
        <div className="data-set-ratio">
            <div className="data-set-ratio__heading-container">
                <h3 className="data-set-ratio__heading">
                    {heading}
                </h3>
            </div>
            <div className="data-set-ratio__chart">
                <div className="data-set-ratio__chart-label-container">
                    <p className="data-set-ratio__chart-label">
                        {label}
                    </p>
                </div>
                <div className="data-set-ratio__segments">
                    {chart()}
                </div>
            </div>
            <div className="data-set-ratio__legend-container">
                {chartLegend()}
            </div>
        </div>
    );
}

DataSetRatio.propTypes = {
    heading: PropTypes.element,
    label: PropTypes.element,
    states: PropTypes.array,
};

export default DataSetRatio;
